import { ConfigElementInfo, ConfigGroupInfo, ConfigValue, PricingRequest } from './types/api-types';
import { conditionApplies } from './conditions';
import { Composition, GroupItem, MAIN_SCENE, OptionValues } from './types/composition';
import { Material, MaterialColor } from './types/catalog-internal';
import { CfgProperty, ElementAccessory, ElementPart, ElementsRoot } from './types/catalog-types';

// TODO review if needed! (not used at 13/06/24)
export function makeInitialPricingRequest(category: string, family: string, variant: string|undefined, sessionid: string|undefined, language: string, values: OptionValues): PricingRequest {
    return {
        config: {
            family,
            sessionid,
            language,
            options: valuesToAPIValues(values),
            elements: []
        },
        config2: {
            family,
            sessionid,
            language,
            elementgroups: []
        },
        state: {
            category,
            family,
            variant,
            groups: []
        }
    }
}

export function calculatePricingRequest(
    // basic data for identification
    category: string, family: string, variant: string|undefined,
    sessionid: string|undefined, language: string,
    // the composition
    composition: Composition, implicitValuesByGroup: Record<string, OptionValues>,
    // reference data
    properties: CfgProperty[],
    qualities: Material[],
    colors: MaterialColor[],
    elements: ElementsRoot
): [
    PricingRequest, 
    boolean, // can call billing service
    boolean, // has any option values (to avoid empty calls)
    string   // motive why billing service cannot be called
] {

    let canCallBillService = true;
    let hasOptionValues = true;
    let motive = '';

    const sceneElementsLegacy: ConfigElementInfo[] = [];
    const billingConfigGroups: ConfigGroupInfo[] = [];

    for(const group of composition.groups) {

        const implicitValues = implicitValuesByGroup[group.id];

        if(group.showIf && !conditionApplies(group.showIf, implicitValues, qualities, [])) {
            continue;
        }

        // calculate the values
        const [ billingValues, groupCanCallBillService, groupMotive ] = calculateGroupBillingValues(group.options, implicitValues, group.scene, properties, colors);
        if(!groupCanCallBillService) {
            canCallBillService = false;
            if(motive === '') {
                motive = groupMotive;
            }
        }
        const billingValuesAPI = valuesToAPIValues(billingValues);
        hasOptionValues = hasOptionValues && (
            billingValuesAPI.length > 0 || 
            billingValuesAPI.some(v => v.propertyvalue !== undefined)
        )

        
        const groupElements = makeAPIElementGroupElements(group, implicitValues, qualities, elements);
        sceneElementsLegacy.push(...groupElements);

        billingConfigGroups.push({
            id: group.id,
            options: billingValuesAPI,
            elements: groupElements
        });

    }

    if(billingConfigGroups.length === 0) {
        canCallBillService = false;
    }

    const billing: PricingRequest = {
        config: {
            family,
            sessionid,
            language,
            options: billingConfigGroups.length > 0 ? billingConfigGroups[0].options : [],
            elements: sceneElementsLegacy
        },
        config2: {
            family,
            sessionid,
            language,
            elementgroups: billingConfigGroups
        },
        state: {
            category,
            family,
            variant,
            ...composition
        }
    }

    return [ billing, canCallBillService, hasOptionValues, motive ];
}

function calculateGroupBillingValues(
    values: OptionValues, implicitValues: OptionValues, scene: string,
    properties: CfgProperty[], colors: MaterialColor[]): [ OptionValues, boolean, string ] {

    const result: OptionValues = {};
    let canCallBillService = true;
    let motive = '';
    for(const property of properties) {

        const serviceUse = property.pricing ?? 'explicit';
        if(serviceUse === 'omit') {
            continue;
        }

        let propertyScenes = [ MAIN_SCENE ];
        if(property.scene) {
            propertyScenes = Array.isArray(property.scene) ? property.scene : [ property.scene ];
        }
        if(!propertyScenes.includes(scene)) {
            continue;
        }

        const type = property.type ?? 'plain';
        let value = serviceUse === 'explicit' ? values[property.name] : implicitValues[property.name];
        let explicitValue = values[property.name];
        if(type === 'material' && value) {
            const [ qualityCode, colorCode ] = value.split('/');
            const material = colors.find(c => c.material === qualityCode && c.code === colorCode);
            if(material) {
                if(property.sendLeatherAccent && material.leatherAccent) {
                    value = material.leatherAccent;
                } else {
                    value = material.assetId;
                }
            }
        }

        result[property.name] = value;

        if(property.requiredForPricing && (explicitValue === undefined || explicitValue === null)) {
            canCallBillService = false;
            if(motive === '') {
                motive = `Property ${property.name} is required for pricing, but it's value is ${JSON.stringify(value)}`;
            }
        }
    }

    return [ result, canCallBillService, motive ];
}

function valuesToAPIValues(values: OptionValues): ConfigValue[] {
    return Object.keys(values).map(key => ({
        propertyid: key,
        propertyvalue: values[key]
    }));
}

function makeAPIElementGroupElements(
    group: GroupItem, implicitValues: OptionValues, 
    qualities: Material[], elements: ElementsRoot
): ConfigElementInfo[] {

    const groupElements: ConfigElementInfo[] = [];
    const accesoriesAdded = new Set<string>();

    for(const element of group.elements) {

        const catalogElement = elements.elements.find(e => e.id === element.element);

        const elementParts = makeAPIElementParts(element.element, implicitValues,
                                                 elements.elementParts, qualities);

        groupElements.push({
            id: element.element,
            data: catalogElement?.data ?? undefined,
            parts: elementParts
        });

        const accesories: ElementAccessory[] = catalogElement?.accesories ?? [];

        for(let accesory of accesories) {

            if(accesoriesAdded.has(accesory.accesoryId)) {
                continue;
            }

            const quantityStr = implicitValues[accesory.quantityProperty];
            const quantity = quantityStr ? Number(quantityStr) : 0;


            if(quantity > 0) {

                const accesoryElement = elements.elements.find(e => e.id === accesory.accesoryId);
                const accesoryParts = makeAPIElementParts(accesory.accesoryId, implicitValues, elements.elementParts, qualities);

                for(let i = 0; i < quantity; i++) {
                    groupElements.push({
                        id: accesory.accesoryId,
                        data: accesoryElement?.data ?? undefined,
                        parts: accesoryParts
                    });
    
                }

                accesoriesAdded.add(accesory.accesoryId);
    
            }

        }

    }

    return groupElements;
}

function makeAPIElementParts(elementId: string, implicitValues: OptionValues, catalogElementParts: ElementPart[], qualities: Material[]) {
    return catalogElementParts.filter(ep => {
        if(ep.element !== elementId) {
            return false;
        }
        return ep.showif ? conditionApplies(ep.showif, implicitValues, qualities, []) : true;
    }).map(ep => {
        let mappedData: any;
        if(typeof ep.data === 'object') {
            mappedData = {};
            for(const [key,value] of Object.entries(ep.data)) {
                if(typeof value === 'string' && value.startsWith('*PROPERTY:')) {
                    const property = value.slice(10);
                    mappedData[key] = implicitValues[property];
                } else {
                    mappedData[key] = value;                        
                }
            }
        } else {
            mappedData = ep.data;
        }
        return {
            id: ep.part,
            role: ep.role,
            data: mappedData
        }
    });
}